import { useState } from "react";
import { Building2, Phone, Users as UsersIcon, Wrench, GripVertical } from "lucide-react";

const COLUMN_META = {
  Kunde: { dot: "bg-success", ring: "border-success/30" },
  Interessent: { dot: "bg-info", ring: "border-info/30" },
  Lieferant: { dot: "bg-muted-foreground", ring: "border-border" },
};

export function CustomerKanban({ rows, categories, onChangeCategory, onOpen }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const handleDrop = (e, cat) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setOverCol(null);
    setDragId(null);
    const row = rows.find((r) => r.id === id);
    if (id && row && (row.category || "Kunde") !== cat) onChangeCategory(id, cat);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="customers-kanban">
      {categories.map((cat) => {
        const meta = COLUMN_META[cat] || COLUMN_META.Lieferant;
        const items = rows.filter((r) => (r.category || "Kunde") === cat);
        return (
          <div
            key={cat}
            onDragOver={(e) => { e.preventDefault(); setOverCol(cat); }}
            onDragLeave={() => setOverCol((c) => (c === cat ? null : c))}
            onDrop={(e) => handleDrop(e, cat)}
            className={`rounded-xl border bg-muted/30 flex flex-col min-h-[240px] transition-colors ${overCol === cat ? "border-primary bg-primary/5" : "border-border"}`}
            data-testid={`kanban-col-${cat}`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                <span className="font-semibold text-sm">{cat}</span>
              </div>
              <span className="text-xs font-medium text-muted-foreground bg-background rounded-full px-2 py-0.5" data-testid={`kanban-count-${cat}`}>{items.length}</span>
            </div>
            <div className="p-2.5 space-y-2.5 flex-1 overflow-y-auto max-h-[70vh]">
              {items.map((r) => (
                <div
                  key={r.id}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData("text/plain", r.id); setDragId(r.id); }}
                  onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  onClick={() => onOpen(r.id)}
                  className={`group rounded-lg border bg-card p-3 cursor-pointer hover:shadow-md hover:border-primary/40 transition-all ${dragId === r.id ? "opacity-50" : ""} ${meta.ring}`}
                  data-testid={`kanban-card-${r.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{r.company_name}</p>
                      {(r.city || r.postal_code) && <p className="text-xs text-muted-foreground truncate">{r.postal_code ? `${r.postal_code} ` : ""}{r.city}</p>}
                    </div>
                    <GripVertical className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground shrink-0" />
                  </div>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                    {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>}
                    <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" />{(r.contacts || []).length}</span>
                    {r.has_maintenance_contract && <span className="inline-flex items-center gap-1 text-success"><Wrench className="w-3 h-3" />Wartung</span>}
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <div className="text-center text-xs text-muted-foreground py-8 border border-dashed border-border rounded-lg">Hierher ziehen</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
